// Filtering helpers for the Jobs page. Active jobs use `jobStatus`, recent jobs use `status`.

// "azsql2_1 835" -> { server: 'azsql2_1', fileType: '835' }; "azsql2_1" -> fileType ''
export const parseJobType = (jobType) => {
    const [server = '', ...rest] = (jobType || '').trim().split(/\s+/);
    return { server, fileType: rest.join(' ') };
};

// Client IDs arrive in mixed case (mdmedstar / MDMEDSTAR), so compare them case-insensitively.
export const clientKey = (clientId) => (clientId || '').toLowerCase();

export const jobStatus = (job) => job.jobStatus || job.status || '';

export const hasError = (job) => Boolean(job.errorMessage) || jobStatus(job) === 'Failed';

export const EMPTY_FILTERS = {
    q: '',
    server: '',
    client: '',
    fileType: '',
    status: '',
    errorsOnly: false
};

// NO_FILE_TYPE selects jobs whose type has no file type suffix (e.g. "azsql2_1").
export const NO_FILE_TYPE = 'none';

export const matchesJob = (job, filters) => {
    const { server, fileType } = parseJobType(job.jobType);
    if (filters.server && server !== filters.server) return false;
    if (filters.client && clientKey(job.clientId) !== filters.client) return false;
    if (filters.fileType) {
        if (filters.fileType === NO_FILE_TYPE ? fileType !== '' : fileType !== filters.fileType) return false;
    }
    if (filters.status && jobStatus(job) !== filters.status) return false;
    if (filters.errorsOnly && !hasError(job)) return false;
    if (filters.q) {
        const q = filters.q.trim().toLowerCase();
        const haystack = [job.jobId, job.jobName, job.jobType, job.clientId, job.currentFileName, job.errorMessage]
            .filter(v => v !== null && v !== undefined)
            .join(' ')
            .toLowerCase();
        if (!haystack.includes(q)) return false;
    }
    return true;
};

// Failed jobs grouped by server/service, most failures first, for the Command Center alert.
// `latest` is the most recent failed job in each group.
export const groupFailedJobs = (jobs) => {
    const groups = new Map();
    for (const job of jobs) {
        if (jobStatus(job) !== 'Failed') continue;
        const server = parseJobType(job.jobType).server || '(unknown)';
        const group = groups.get(server) || { server, count: 0, latest: null };
        group.count++;
        if (!group.latest || (job.startTime || '') > (group.latest.startTime || '')) group.latest = job;
        groups.set(server, group);
    }
    return [...groups.values()].sort((a, b) => b.count - a.count || a.server.localeCompare(b.server));
};

// Dropdown options built from the jobs currently loaded.
export const buildFilterOptions = (jobs) => {
    const servers = new Set();
    const fileTypes = new Set();
    const statuses = new Set();
    const clientSpellings = new Map(); // key -> Map(spelling -> count)
    let hasNoFileType = false;

    for (const job of jobs) {
        const { server, fileType } = parseJobType(job.jobType);
        if (server) servers.add(server);
        if (fileType) fileTypes.add(fileType); else hasNoFileType = true;
        if (jobStatus(job)) statuses.add(jobStatus(job));
        if (job.clientId) {
            const key = clientKey(job.clientId);
            if (!clientSpellings.has(key)) clientSpellings.set(key, new Map());
            const spellings = clientSpellings.get(key);
            spellings.set(job.clientId, (spellings.get(job.clientId) || 0) + 1);
        }
    }

    // Label each client with its most common spelling
    const clients = [...clientSpellings].map(([value, spellings]) => ({
        value,
        label: [...spellings].sort((a, b) => b[1] - a[1])[0][0]
    }));

    const byText = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    return {
        servers: [...servers].sort(byText),
        clients: clients.sort((a, b) => byText(a.label, b.label)),
        fileTypes: [...[...fileTypes].sort(byText).map(t => ({ value: t, label: t })),
            ...(hasNoFileType ? [{ value: NO_FILE_TYPE, label: '(none)' }] : [])],
        statuses: [...statuses].sort(byText)
    };
};
